export type NumericFilterOperator = 'eq' | 'ne' | 'gt' | 'ge' | 'lt' | 'le';

export type NumericColumnFilter =
    | { readonly kind: 'compare'; readonly operator: NumericFilterOperator; readonly value: number }
    | { readonly kind: 'range'; readonly min: number; readonly max: number };

// Longest prefixes first so `>=` is not read as `>` followed by `=5`.
const OPERATOR_PREFIXES: ReadonlyArray<readonly [prefix: string, operator: NumericFilterOperator]> = [
    ['>=', 'ge'],
    ['<=', 'le'],
    ['!=', 'ne'],
    ['<>', 'ne'],
    ['>', 'gt'],
    ['<', 'lt'],
    ['=', 'eq'],
];

const RANGE_PATTERN = /^(.+?)\s*(?:\.\.|-)\s*(.+)$/;

/** Non-negative decimal, tolerating a leading `$` and thousands separators. */
function parseAmount(text: string): number | null {
    const cleaned = text.trim().replace(/^\$/, '').replaceAll(',', '');
    if (!/^(\d+(\.\d*)?|\.\d+)$/.test(cleaned)) return null;
    return Number(cleaned);
}

/**
 * Parses the text typed into a numeric column filter. A bare number matches
 * exactly, a comparison prefix (`>`, `>=`, `<`, `<=`, `=`, `!=`) compares,
 * and `a-b` or `a..b` bounds inclusively. Anything else (including an
 * inverted range) is `null` so the caller can flag it instead of sending it.
 * Values are quantities and prices, so they are non-negative, which keeps
 * the `a-b` range spelling unambiguous.
 */
export function parseNumericColumnFilter(text: string): NumericColumnFilter | null {
    const trimmed = text.trim();
    if (trimmed === '') return null;
    for (const [prefix, operator] of OPERATOR_PREFIXES) {
        if (trimmed.startsWith(prefix)) {
            const value = parseAmount(trimmed.slice(prefix.length));
            return value === null ? null : { kind: 'compare', operator, value };
        }
    }
    const exact = parseAmount(trimmed);
    if (exact !== null) return { kind: 'compare', operator: 'eq', value: exact };
    const range = RANGE_PATTERN.exec(trimmed);
    if (!range) return null;
    const min = parseAmount(range[1]);
    const max = parseAmount(range[2]);
    if (min === null || max === null || min > max) return null;
    return { kind: 'range', min, max };
}

/** Renders a parsed filter as an OData predicate over `field`. */
export function numericColumnFilterToOData(field: string, filter: NumericColumnFilter): string {
    if (filter.kind === 'range') return `${field} ge ${filter.min} and ${field} le ${filter.max}`;
    return `${field} ${filter.operator} ${filter.value}`;
}
